import app from 'flarum/forum/app';
import Model, {SavedModelData} from 'flarum/common/Model';
import User from 'flarum/common/models/User';
import ItemList from 'flarum/common/utils/ItemList';
import Badge from 'flarum/common/components/Badge';
import Album from './Album';

export type ImageInfo = [string, number, number];

export interface StickerPlacement {
    type: string
    left: number
    top: number
    color?: string
    width?: number
    height?: number
    radius?: number
    angle?: number
}

export interface PublicStickerPlacement extends StickerPlacement {
    url?: string
    label?: string
}

export interface Crop {
    top: number
    left: number
    width: number
    height: number
}

export default class Picture extends Model {
    title = Model.attribute<string>('title')
    description = Model.attribute<string>('description')
    license = Model.attribute<string | null>('license')
    author = Model.attribute<string>('author')
    takenAt = Model.attribute('takenAt', Model.transformDate)
    exif = Model.attribute<{ [key: string]: number | string | string[] } | undefined>('exif')
    geo = Model.attribute<{ lat: number, lng: number } | null | undefined>('geo')
    isApproved = Model.attribute<boolean>('isApproved')
    isDraft = Model.attribute<boolean>('isDraft')
    createdAt = Model.attribute('createdAt', Model.transformDate)
    isHidden = Model.attribute<boolean>('isHidden')
    hiddenAt = Model.attribute('hiddenAt', Model.transformDate)
    isFavorite = Model.attribute<boolean>('isFavorite')
    stickers = Model.attribute<PublicStickerPlacement[] | null>('stickers')
    editStickers = Model.attribute<StickerPlacement[] | null>('editStickers')
    crop = Model.attribute<Crop | null>('crop')

    imageUrls = Model.attribute<{ [number: string]: ImageInfo }>('imageUrls')

    canRename = Model.attribute<boolean>('canRename')
    canLicense = Model.attribute<boolean>('canLicense')
    canEditImage = Model.attribute<boolean>('canEditImage')
    canApprove = Model.attribute<boolean>('canApprove')
    canFavorite = Model.attribute<boolean>('canFavorite')
    canHide = Model.attribute<boolean>('canHide')
    canRestore = Model.attribute<boolean>('canRestore')
    canDelete = Model.attribute<boolean>('canDelete')

    user = Model.hasOne<User>('user')
    album = Model.hasOne<Album>('album')

    imageUrl(size: number): string | null {
        const image = this.imageUrls()[size];
        return image ? image[0] : null;
    }

    // Selects the biggest image possible that matches the query
    bestImage(size: number): ImageInfo {
        let finalSize = this.imageUrls()[150]; // 150 should always exist

        [2048, 1400, 800, 400].some((possibleSize: number) => {
            if (possibleSize <= size && this.imageUrls()[possibleSize]) {
                finalSize = this.imageUrls()[possibleSize];
                return true;
            }

            return false;
        });

        return finalSize;
    }

    badges() {
        const items = new ItemList();

        if (this.isHidden()) {
            items.add('hidden', Badge.component({
                type: 'hidden',
                icon: 'fas fa-trash',
                label: app.translator.trans('kilowhat-cimaise.forum.badge.hidden'),
            }));
        }

        if (!this.isApproved()) {
            items.add('awaitingApproval', Badge.component({
                type: 'awaitingApproval',
                icon: 'fas fa-gavel',
                label: app.translator.trans('kilowhat-cimaise.forum.badge.awaiting-approval'),
            }));
        }

        if (this.isFavorite()) {
            items.add('favorite', Badge.component({
                type: 'favorite',
                icon: 'fas fa-star',
                label: app.translator.trans('kilowhat-cimaise.forum.badge.favorite'),
            }));
        }

        return items;
    }

    apiEndpoint() {
        return '/cimaise/pictures' + (this.exists ? '/' + (this.data as SavedModelData).id : '');
    }
}
